import { useLocation, useNavigate } from "react-router-dom";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";

import { allSharedComponents } from "../../../../shared";
import UpcomingEventsList from "./Tabs/UpcomingEventsList";
import PreviousEventsList from "./Tabs/PreviousEventsList";
import {
  Directions,
  EVENT_STATUS,
  eventConstants,
  paymentType,
  sportConstants,
  TabsConstants,
  timezoneConstants,
  translationConstants,
} from "../../../../constants";
import {
  EventActions,
  SubscriptionActions,
  authActions,
  paymentActions,
} from "../../../../redux/actions";
import {
  getUserPayload,
  dateToUTCWithTimezone,
  getEpochMonthStartAndEndDate,
  unloadWidget,
} from "../../../../system";
import {
  ArrowLeftFilledIcon,
  ArrowRightFilledIcon,
} from "../../../../assets/images/icon";

const { TimezoneDisplayText, CustomModal, RegistrationPaymentModal, BaseForm } =
  allSharedComponents;

const getInitialGridState = (
  selectedTab,
  eventFilter = "",
  cityId = "",
  dateFilter = "",
  sportFilter = ""
) => {
  let filterQuery =
    selectedTab === TabsConstants.NUMERIC_UPCOMING
      ? `status != ${EVENT_STATUS.Expired}`
      : `status == ${EVENT_STATUS.Expired}`;
  if (eventFilter === eventConstants.PRIVATE) {
    filterQuery += " && isPublicRegistrationAllowed = false";
  } else if (eventFilter === eventConstants.PUBLIC) {
    filterQuery += " && isPublicRegistrationAllowed = true";
  }

  if (cityId) {
    filterQuery += ` && (cityId = ${cityId} || spotInfo.spotCityId = ${cityId})`;
  }

  if (dateFilter) {
    const { epochMonthWithFirstDate, epochMonthWithLastDate } =
      getEpochMonthStartAndEndDate(dateFilter);
    filterQuery += ` && (registrationStartDate <= ${epochMonthWithLastDate} && endDate >= ${epochMonthWithFirstDate})`;
  }

  if (sportFilter) {
    filterQuery += ` && sports.Contains(${sportFilter})`;
  }

  return {
    length: 7,
    search: null,
    pageNumber: 1,
    orderBy: "startDate asc",
    filterQuery: filterQuery,
  };
};

function PublicEvents() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [urlCheckoutId, setUrlCheckoutId] = useState(
    new URLSearchParams(location?.search)?.get("id")
  );

  const [key, setKey] = useState(TabsConstants.NUMERIC_UPCOMING);
  const [eventFilter, setEventFilter] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [gridState, setGridState] = useState(
    getInitialGridState(
      TabsConstants.NUMERIC_UPCOMING,
      eventFilter,
      selectedCity,
      dateToUTCWithTimezone(),
      sportFilter
    )
  );
  const [upcomingEventPageData, setUpcomingEventPageData] = useState([]);
  const [previousEventPageData, setPreviousEventPageData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const userPayload = getUserPayload();
  const [isShow, setShow] = useState(false);
  const [dateFilter, setDateFilter] = useState(dateToUTCWithTimezone());

  const isParticipantsValidated = useSelector(
    (state) => state.eventReducer?.isParticipantsValidated
  );
  const cities = useSelector((state) => state.LoginReducer.cities);
  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );
  const isAuthenticated = useSelector(
    (state) => state.LoginReducer.isAuthenticated
  );
  const events = useSelector(
    (state) => state.eventReducer.publicEvents?.pageData
  );
  const totalEvents = useSelector(
    (state) => state.eventReducer.publicEvents?.page?.totalElements
  );
  const EventData = useSelector((state) => state.eventReducer.eventDataById);

  useEffect(() => {
    if (isParticipantsValidated) {
      if (!EventData?.totalFee) {
        dispatch(
          EventActions.addEventParticipant(
            {
              systemUserId: userPayload?.user?.id,
              eventId: selectedEvent,
            },
            setShow,
            () => {
              resetAllStates();
              dispatch(EventActions.getPaginatedPublicEvents(gridState));
            }
          )
        );
      } else {
        onSetShow();
      }
    }
    //eslint-disable-next-line
  }, [isParticipantsValidated]);

  useEffect(() => {
    dispatch(authActions.fetchCities(process.env.REACT_APP_Country_Id));
    //eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    dispatch(EventActions.getPaginatedPublicEvents(gridState));
    //eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    if (key === TabsConstants.NUMERIC_UPCOMING) {
      setUpcomingEventPageData(events);
    } else {
      setPreviousEventPageData(events);
    }
    //eslint-disable-next-line
  }, [events]);

  const onSetShow = () => {
    setShow((prev) => !prev);
    if (isShow) {
      resetAllStates();
    }
    if (!isAuthenticated) {
      dispatch(authActions.setShowLoginModal(true));
      dispatch(authActions.setLoginFromRegistration(true));
      setSelectedEvent(null);
    }
  };

  const resetAllStates = () => {
    unloadWidget();
    setSelectedEvent(null);
    dispatch(EventActions.setValidateParticipantsFalse());
    dispatch(paymentActions.paymentCheckoutNull());
  };

  const handleTabChange = (selectedTab) => {
    setKey(selectedTab);

    let orderBy;
    if (selectedTab === TabsConstants.NUMERIC_UPCOMING) {
      orderBy = "startDate asc";
    } else {
      orderBy = "startDate desc";
    }
    const updatedGridState = getInitialGridState(
      selectedTab,
      eventFilter,
      selectedCity,
      dateFilter,
      sportFilter
    );

    setGridState({ ...updatedGridState, orderBy });
  };

  const handleEventFilterChange = (event) => {
    const filter = event.target.value;
    setEventFilter(filter);
    const updatedGridState = getInitialGridState(
      key,
      filter,
      selectedCity,
      dateFilter,
      sportFilter
    );
    setGridState(updatedGridState);
  };

  const handleSportFilterChange = (event) => {
    const filter = event.target.value;
    setSportFilter(filter);
    const updatedGridState = getInitialGridState(
      key,
      eventFilter,
      selectedCity,
      dateFilter,
      filter
    );
    setGridState(updatedGridState);
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setSelectedCity(cityId);
    const updatedGridState = getInitialGridState(
      key,
      eventFilter,
      cityId,
      dateFilter,
      sportFilter
    );
    setGridState(updatedGridState);
  };

  const getMoreEvents = () => {
    const updatedGridState = { ...gridState, length: gridState.length + 7 };
    setGridState(updatedGridState);
  };

  useEffect(() => {
    if (urlCheckoutId) {
      navigate(location.pathname);
      setUrlCheckoutId(null);
      dispatch(
        EventActions.addParticipantWithPayment(urlCheckoutId, () => {
          dispatch(paymentActions.paymentCheckoutNull());
          dispatch(EventActions.getPaginatedPublicEvents(gridState));
        })
      );
    }
    //eslint-disable-next-line
  }, [urlCheckoutId]);

  useEffect(() => {
    if (selectedEvent) {
      if (isAuthenticated) {
        dispatch(
          EventActions.getEventById(selectedEvent, () => {
            dispatch(
              EventActions.validateParticipentsForEvents(
                userPayload?.user?.id,
                selectedEvent,
                setSelectedEvent
              )
            );
          })
        );
      } else {
        onSetShow();
      }
    }
    //eslint-disable-next-line
  }, [selectedEvent, isAuthenticated]);

  const handleClearFilters = () => {
    setSelectedCity("");
    setEventFilter("");
    setSportFilter("");
    setDateFilter(dateToUTCWithTimezone());
    const updatedGridState = getInitialGridState(key, "", "");
    setGridState(updatedGridState);
  };

  useEffect(() => {
    const updatedGridState = getInitialGridState(
      key,
      eventFilter,
      selectedCity,
      dateFilter,
      sportFilter
    );
    setGridState(updatedGridState);
    //eslint-disable-next-line
  }, [dateFilter]);

  const nextYear = () => {
    let newDate;
    setDateFilter((prevDate) => {
      newDate = dateToUTCWithTimezone(prevDate).add(1, "month");
      return newDate;
    });
    const updatedGridState = getInitialGridState(
      key,
      eventFilter,
      selectedCity,
      newDate,
      sportFilter
    );
    setGridState(updatedGridState);
  };

  const prevYear = () => {
    let newDate;
    setDateFilter((prevDate) => {
      newDate = dateToUTCWithTimezone(prevDate).subtract(1, "month");
      return newDate;
    });
    const updatedGridState = getInitialGridState(
      key,
      eventFilter,
      selectedCity,
      newDate,
      sportFilter
    );
    setGridState(updatedGridState);
  };

  useEffect(() => {
    dispatch(
      SubscriptionActions.GetAllSubscriptionPrices({ isLoading: false })
    );
    //eslint-disable-next-line
  }, []);

  return (
    <div className="container UserDiv pe-4">
      <div className="row d-flex py-2 flex-column flex-md-row">
        <div className="col-3">
          <p className="UserTabHeading m-0">{t(translationConstants.events)}</p>
        </div>
        <div className="col-lg-9 col-12 d-flex flex-column flex-lg-row gap-2 justify-content-lg-end justify-content-center align-items-center px-3">
          <div className="d-flex justify-content-center justify-content-lg-end mx-2">
            {direction === Directions.rtl ? (
              <img
                src={ArrowRightFilledIcon}
                alt="center-logo"
                onClick={nextYear}
              />
            ) : (
              <img
                src={ArrowLeftFilledIcon}
                alt="center-logo"
                onClick={prevYear}
              />
            )}
            <span className="px-2">
              {dateFilter.format(timezoneConstants.monthYearFullWordFormat)}
            </span>
            {direction === Directions.rtl ? (
              <img
                src={ArrowLeftFilledIcon}
                alt="center-logo"
                onClick={prevYear}
              />
            ) : (
              <img
                src={ArrowRightFilledIcon}
                alt="center-logo"
                onClick={nextYear}
              />
            )}
          </div>

          <div className="col-lg-2 col-md-12 col-12 d-flex gap-2 justify-content-center justify-content-md-end align-items-center">
            <Form.Select
              className="custom-select-icon filter-style"
              aria-label="Event filter select"
              onChange={handleEventFilterChange}
              value={eventFilter}
            >
              <option value="">
                {t(translationConstants.SHOW_ALL_EVENTS)}
              </option>
              <option value={eventConstants.PUBLIC}>
                {t(translationConstants.PUBLIC_EVENTS)}
              </option>
              <option value={eventConstants.PRIVATE}>
                {t(translationConstants.PRIVATE_EVENTS)}
              </option>
            </Form.Select>
          </div>

          <div className="col-lg-2 col-md-12 col-12 d-flex gap-2 justify-content-center justify-content-md-end align-items-center">
            <Form.Select
              className="custom-select-icon filter-style"
              aria-label="Event filter select"
              onChange={handleSportFilterChange}
              value={sportFilter}
            >
              <option value="">{t(translationConstants.sportType)}</option>
              {Object.entries(sportConstants).map(([sport, value]) => (
                <option key={value} value={value}>
                  {t(translationConstants[sport])}
                </option>
              ))}
            </Form.Select>
          </div>

          <div className="col-lg-2 col-12 d-flex justify-content-center justify-content-md-end align-items-center">
            <Form.Select
              className="custom-select-icon filter-style"
              aria-label="City filter select"
              onChange={handleCityChange}
              value={selectedCity}
            >
              <option value="">{t(translationConstants.selectcity)}</option>
              {cities?.map((city) => (
                <option key={city.id} value={city.id}>
                  {direction === Directions.rtl
                    ? city?.arabicName || city?.name
                    : city?.name}
                </option>
              ))}
            </Form.Select>
          </div>

          {(selectedCity ||
            eventFilter ||
            sportFilter ||
            !dateFilter.isSame(dateToUTCWithTimezone(), "month")) && (
            <div className="col-lg-2 col-md-12 col-12 d-flex gap-2 justify-content-center justify-content-lg-end align-items-center w-auto">
              <div className="cancel-filter" onClick={handleClearFilters}>
                {t(translationConstants.CLEAR_FILTERS)}
              </div>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="view-center-profile-scroll-tabs">
          <Tabs
            id="controlled-tab-example"
            selectedIndex={key}
            onSelect={handleTabChange}
          >
            <div className="row d-flex align-items-center">
              <div className="col-md-8 col-12 ">
                <TabList>
                  <Tab eventKey={TabsConstants.NUMERIC_UPCOMING}>
                    {t(translationConstants.UPCOMING)}
                  </Tab>
                  <Tab eventKey={TabsConstants.NUMERIC_PREVIOUS}>
                    {t(translationConstants.PREVIOUS)}
                  </Tab>
                </TabList>
              </div>
            </div>
            <TabPanel>
              <TimezoneDisplayText />
              <UpcomingEventsList
                events={upcomingEventPageData}
                getMoreEvents={getMoreEvents}
                totalEvents={totalEvents}
                setSelectedEvent={setSelectedEvent}
              />
            </TabPanel>
            <TabPanel>
              <TimezoneDisplayText />
              <PreviousEventsList
                events={previousEventPageData}
                getMoreEvents={getMoreEvents}
                totalEvents={totalEvents}
              />
            </TabPanel>
          </Tabs>
        </div>
      </div>
      {isAuthenticated && (
        <CustomModal
          size="sm"
          show={isShow}
          scrollable={true}
          setShow={onSetShow}
        >
          <BaseForm customClass="payment-modal-min-height">
            <RegistrationPaymentModal
              title={t(translationConstants.REGISTER_FOR_EVENT)}
              eventID={selectedEvent}
              type={paymentType.Event}
            />
          </BaseForm>
        </CustomModal>
      )}
    </div>
  );
}

export default PublicEvents;
